import { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import { PhoneFilters, PhoneBookEntry, ShivarOption } from '../../types';
import { useContacts } from '../../hooks/useContacts';
import { FilterBar } from '../../components/FilterBar';
import { PhoneBookTable } from '../../components/PhoneBookTable';
import { PhoneBookCard } from '../../components/PhoneBookCard';
import { Loading } from '../../components/ui/Loading';
import { Empty } from '../../components/ui/Empty';
import { ErrorAlert } from '../../components/ui/ErrorAlert';
import { formatShivar } from '../../utils/shivar';

export const EmployeeContacts = () => {
  const [filters, setFilters] = useState<PhoneFilters>({});
  const [page, setPage] = useState(1);
  const [options, setOptions] = useState({ shivars: [] as ShivarOption[] });
  const [optionsLoading, setOptionsLoading] = useState(true);
  const [optionsError, setOptionsError] = useState<string | null>(null);

  const fetchOptions = async () => {
    setOptionsLoading(true);
    setOptionsError(null);
    try {
      const { data, error: dbError } = await supabase.from('shivars').select('name, marathi_name').order('name');
      if (dbError) throw dbError;
      setOptions({
        shivars: (data ?? []).map((s) => ({ value: s.name, label: formatShivar(s.name, s.marathi_name) })),
      });
    } catch (err) {
      setOptionsError(err instanceof Error ? err.message : 'Failed to load shivars');
    } finally {
      setOptionsLoading(false);
    }
  };

  useEffect(() => {
    fetchOptions();
  }, []);

  const { contacts, count, loading, error, pageSize } = useContacts({ ...filters, page });
  const totalPages = Math.ceil(count / pageSize);
  const shivarMap = Object.fromEntries(options.shivars.map((s) => [s.value, s.label]));

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Contact List</h1>
        <p className="text-sm text-gray-600">View and edit phone book contacts.</p>
      </div>
      {optionsError && <ErrorAlert message={optionsError} onRetry={fetchOptions} />}
      {optionsLoading ? <Loading /> : <FilterBar filters={filters} onChange={(f) => { setFilters(f); setPage(1); }} options={options} />}
      {error && <ErrorAlert message={error} onRetry={() => window.location.reload()} />}
      {loading ? (
        <Loading />
      ) : contacts.length === 0 ? (
        <Empty title="No contacts found" message="Try adjusting your search or filters." />
      ) : (
        <>
          <div className="hidden md:block">
            <PhoneBookTable contacts={contacts} shivarMap={shivarMap} showEdit />
          </div>
          <div className="grid grid-cols-1 gap-4 md:hidden">
            {contacts.map((contact: PhoneBookEntry) => (
              <PhoneBookCard key={contact.id} contact={contact} shivarMap={shivarMap} showEdit />
            ))}
          </div>
        </>
      )}
      {totalPages > 1 && (
        <div className="flex justify-center gap-2 pt-4">
          <button
            onClick={() => setPage((p: number) => Math.max(1, p - 1))}
            disabled={page === 1}
            className="rounded border border-gray-300 px-3 py-1 text-sm disabled:opacity-50"
          >
            Prev
          </button>
          <span className="px-2 py-1 text-sm text-gray-700">{page} / {totalPages}</span>
          <button
            onClick={() => setPage((p: number) => Math.min(totalPages, p + 1))}
            disabled={page === totalPages}
            className="rounded border border-gray-300 px-3 py-1 text-sm disabled:opacity-50"
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
};
